import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FlexBodyEmail } from '../../../components/FlexBodyEmail/FlexBodyEmail';
import MessagePreview from '../../../components/MessagePreview/MessagePreview';
import SimpleHeader from 'components/Headers/SimpleHeader.js';
import ReactButton from 'components/Buttons/ReactButton';
import { ErrorMessage } from '@hookform/error-message';
import { ScreenPrompt } from 'components/ScreenPrompt';
import FlexSelect from 'components/Inputs/FlexSelect';
import { useForm, Controller } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import Service from '../service';
import {
  Container,
  Row,
  Card,
  Form,
  Col,
  Input,
  CardHeader,
  CardBody,
  Badge
} from 'reactstrap';
import { useEntityCustomFieldsByRecordType } from 'views/fieldConfigs/hooks/useCustomFieldsByRecordType';
import { useProtocolDefaultFieldsSettings } from 'views/fieldConfigs/hooks/useProtocolDefaultFieldsSettings';

const standardFieldMap = {
  title: { label: 'Título do Protocolo', variableName: 'Titulo' },
  client_id: { label: 'Nome do Cliente', variableName: 'ClienteNome' },
  telephone: { label: 'Telefone', variableName: 'Telefone' },
  description: { label: 'Descrição', variableName: 'Descricao' },
  reason_id: { label: 'Motivo', variableName: 'Motivo' },
  subreason_id: { label: 'Submotivo', variableName: 'Submotivo' },
  service_channel: {
    label: 'Canal de Atendimento',
    variableName: 'CanalAtendimento'
  },
  current_status: { label: 'Status/Fase', variableName: 'Status' },
  current_responsible: {
    label: 'Nome do Responsável',
    variableName: 'ResponsavelNome'
  },
  account_id: { label: 'Nome da Conta', variableName: 'ContaNome' },

  N_Protocolo: { label: 'Número do Protocolo', variableName: 'N_Protocolo' },
  DataHora: { label: 'Data e Hora', variableName: 'DataHora' }
};

const ConfigStatusNotificationMessages = (props) => {
  const history = useHistory();
  const { id } = props.match.params;

  const initialRecordTypeRef = useRef(null);
  const quillRef = useRef(null);

  const { reset, control, handleSubmit, errors, formState, setValue, watch } =
    useForm();

  const selectedRecordTypeId = watch('record_type');

  const { customFields, getCustomFields, isLoadingCustomFields } =
    useEntityCustomFieldsByRecordType('PROTOCOL', selectedRecordTypeId);

  const {
    protocolDefaultFieldsDisplaySettings,
    isLoadingProtocolDefaultFields
  } = useProtocolDefaultFieldsSettings(selectedRecordTypeId);

  useEffect(() => {
    if (selectedRecordTypeId) {
      getCustomFields();
    }
  }, [selectedRecordTypeId, getCustomFields]);

  useEffect(() => {
    if (selectedRecordTypeId) {
      if (initialRecordTypeRef.current === null) {
        initialRecordTypeRef.current = selectedRecordTypeId;
      } else if (initialRecordTypeRef.current !== selectedRecordTypeId) {
        if (quillRef.current && quillRef.current.getEditor) {
          const quill = quillRef.current.getEditor();
          quill.setText('');
        }
        setValue('html_message', '', { shouldDirty: true });
        initialRecordTypeRef.current = selectedRecordTypeId;
      }
    }
  }, [selectedRecordTypeId, setValue]);

  const availableVariables = useMemo(() => {
    const mappedStandardFields = [];
    if (protocolDefaultFieldsDisplaySettings) {
      Object.keys(protocolDefaultFieldsDisplaySettings).forEach((fieldKey) => {
        const settings = protocolDefaultFieldsDisplaySettings[fieldKey];
        const mapping = standardFieldMap[fieldKey];

        if (settings && settings.isActive && mapping) {
          mappedStandardFields.push({
            key: `{{${mapping.variableName}}}`,
            name: mapping.label,
            group: 'Campos Padrão'
          });
        }
      });
    }

    mappedStandardFields.push({
      key: `{{${standardFieldMap.N_Protocolo.variableName}}}`,
      name: standardFieldMap.N_Protocolo.label,
      group: 'Campos Padrão'
    });
    mappedStandardFields.push({
      key: `{{${standardFieldMap.DataHora.variableName}}}`,
      name: standardFieldMap.DataHora.label,
      group: 'Campos Padrão'
    });

    let mappedCustomFields = [];
    if (customFields && customFields.length > 0 && selectedRecordTypeId) {
      mappedCustomFields = customFields
        .filter((field) => {
          if (!field.recordTypesConfig) {
            return false;
          }

          if (!Array.isArray(field.recordTypesConfig)) {
            return (
              field.recordTypesConfig.recordTypeId === selectedRecordTypeId
            );
          }

          return field.recordTypesConfig.some(
            (rt) => rt.recordTypeId === selectedRecordTypeId
          );
        })
        .map((ccf) => ({
          key: `{{${ccf.name}}}`,
          name: ccf.label,
          group: 'Campos Personalizados'
        }));
    }

    return [...mappedStandardFields, ...mappedCustomFields];
  }, [
    customFields,
    selectedRecordTypeId,
    protocolDefaultFieldsDisplaySettings
  ]);

  const insertVariableAtCursor = (variable) => {
    if (quillRef.current && quillRef.current.getEditor) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection(true);
      const insertPosition = range
        ? range.index + range.length
        : quill.getLength();

      quill.insertText(insertPosition, `${variable} `, 'user');
      quill.setSelection(insertPosition + variable.length + 1);

      setValue('html_message', quill.root.innerHTML, { shouldDirty: true });
    }
  };

  const { isDirty } = formState;
  const [isUpdate, setIsUpdate] = useState(false);
  const [recordTypesProtocol, setRecordTypesProtocol] = useState([]);

  const getRecordTypesProtocol = async () => {
    const response = await Service.getRecordTypesProtocol();
    setRecordTypesProtocol(response?.length ? response : []);
  };

  const getInitialData = async () => {
    await getRecordTypesProtocol();
    if (id) {
      Service.get(id)
        .then((res) => {
          const statusData = res?.data?.content;
          reset({
            name: statusData.name || '',
            html_message: statusData.html_message || '',
            record_type: statusData.record_type || null
          });
          setIsUpdate(true);
        })
        .catch((error) => {
          console.error(error);
          toast.error('Não foi possível carregar as informações da mensagem.', {
            autoClose: 3000,
            closeOnClick: true
          });
        });
    }
  };

  useEffect(() => {
    getInitialData();
  }, [id, reset]);

  const onSubmit = async (data) => {
    try {
      const formatedData = { ...data };
      const status = isUpdate
        ? await Service.update(id, formatedData)
        : await Service.create(formatedData);
      history.push('/admin/status/messages/notification/list');
      toast.success(status.data.message, {
        autoClose: 3000,
        closeOnClick: true
      });
    } catch (err) {
      const { data } = err.response;
      toast.error(data.message, {
        autoClose: 3000,
        closeOnClick: true
      });
    }
  };

  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  const cancelAction = () => {
    history.push('/admin/status/messages/notification/list');
  };

  const standardVars = availableVariables.filter(
    (v) => v.group === 'Campos Padrão'
  );
  const customVars = availableVariables.filter(
    (v) => v.group === 'Campos Personalizados'
  );

  return (
    <>
      <SimpleHeader
        name="Formulário de Mensagem"
        parentName="Mensagens"
        returnPath="/admin/status/messages/notification/list"
        buttonTitle="Voltar para listagem"
      />
      <ScreenPrompt
        when={isDirty && !formState.isSubmitting}
        message={`Está certo de que deseja sair? Terá que preencher os dados novamente quando retornar`}
      />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">Mensagens de notificação de email</h3>
                </CardHeader>
                <CardBody>
                  <Form
                    className="needs-validation"
                    onSubmit={handleSubmit(onSubmit)}
                  >
                    <Row className="mb-3">
                      <Col md={6} sm={12}>
                        <label className="form-control-label">Nome*</label>
                        <Controller
                          as={Input}
                          control={control}
                          name="name"
                          rules={{ required: 'Campo obrigatório' }}
                          defaultValue=""
                        />
                        <ErrorMessage
                          errors={errors}
                          name="name"
                          render={({ message }) => errorFormMessage(message)}
                        />
                      </Col>
                      <Col md={6} sm={12}>
                        <label className="form-control-label">
                          Tipo de Registro*
                        </label>
                        <Controller
                          render={(props) => (
                            <FlexSelect
                              isClearable={true}
                              dataOptions={recordTypesProtocol || null}
                              isMulti={false}
                              closeMenuOnSelect={true}
                              value={props.value}
                              valueController={(fieldName, value) =>
                                setValue(fieldName, value)
                              }
                              fieldName="record_type"
                              labelName="label"
                              valueName="value"
                            />
                          )}
                          control={control}
                          name="record_type"
                          rules={{
                            required: 'Tipo de registro é obrigatório!'
                          }}
                          defaultValue=""
                        />
                        <ErrorMessage
                          errors={errors}
                          name="record_type"
                          render={({ message }) => errorFormMessage(message)}
                        />
                      </Col>
                    </Row>
                    <Row className="mb-3" style={{ alignItems: 'flex-start' }}>
                      <Col md={7} sm={12}>
                        <label className="form-control-label">
                          Mensagem de notificação por e-mail*
                        </label>
                        <Controller
                          name="html_message"
                          control={control}
                          defaultValue=""
                          render={(props) => (
                            <FlexBodyEmail
                              value={props.value}
                              defaultValue={props.value}
                              fieldName={props.name}
                              setValue={setValue}
                              ref={quillRef}
                            />
                          )}
                        />
                      </Col>
                      <Col md={5} sm={12} className="mt-4 mt-md-0">
                        <label className="form-control-label">
                          Visualização da mensagem:
                        </label>
                        <div
                          style={{
                            border: '1px solid #e6e6e6',
                            borderRadius: 6,
                            minHeight: 80,
                            padding: 12,
                            background: '#fafbfc',
                            fontSize: 14,
                            marginTop: 4,
                            wordBreak: 'break-word'
                          }}
                          tabIndex={0}
                        >
                          <MessagePreview html={watch('html_message')} />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12} sm={12}>
                        <label className="form-control-label">
                          Variáveis disponíveis para uso na mensagem:
                        </label>

                        {isLoadingProtocolDefaultFields ? (
                          <p className="small text-muted">
                            Carregando campos padrão...
                          </p>
                        ) : (
                          standardVars.length > 0 &&
                          selectedRecordTypeId && (
                            <div>
                              <h6 className="text-muted small mt-2">
                                Campos Padrões
                              </h6>
                              <div
                                style={{
                                  display: 'flex',
                                  gap: 6,
                                  flexWrap: 'wrap'
                                }}
                              >
                                {standardVars.map((variable) => (
                                  <Badge
                                    key={variable.key}
                                    pill={false}
                                    color="primary"
                                    style={{
                                      textTransform: 'none',
                                      cursor: 'pointer',
                                      fontSize: '0.8rem',
                                      fontWeight: 500
                                    }}
                                    onClick={() =>
                                      insertVariableAtCursor(variable.key)
                                    }
                                    title={`Inserir ${variable.name}`}
                                  >
                                    {variable.name}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )
                        )}

                        {isLoadingCustomFields ? (
                          <p className="small text-muted">
                            Carregando campos personalizados...
                          </p>
                        ) : (
                          customVars.length > 0 && (
                            <div>
                              <h6 className="text-muted small mt-2">
                                Campos Personalizados
                              </h6>
                              <div
                                style={{
                                  display: 'flex',
                                  gap: 6,
                                  flexWrap: 'wrap'
                                }}
                              >
                                {customVars.map((variable) => (
                                  <Badge
                                    key={variable.key}
                                    pill={false}
                                    color="primary"
                                    style={{
                                      textTransform: 'none',
                                      cursor: 'pointer',
                                      fontSize: '0.8rem',
                                      fontWeight: 500
                                    }}
                                    onClick={() =>
                                      insertVariableAtCursor(variable.key)
                                    }
                                    title={`Inserir ${variable.name}`}
                                  >
                                    {variable.name}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )
                        )}
                      </Col>
                    </Row>

                    <hr />

                    <ReactButton
                      btnColor="confirmation"
                      type="submit"
                      disabled={!isDirty}
                    >
                      Salvar
                    </ReactButton>

                    <ReactButton
                      btnColor="cancelation"
                      onClick={() => cancelAction()}
                    >
                      Cancelar
                    </ReactButton>
                  </Form>
                </CardBody>
              </Card>
            </div>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default ConfigStatusNotificationMessages;
